import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from '../../../lib/supabase';

export default function ProfileTab({ user, profile, onSave, showToast }) {
  const [form, setForm] = useState({
    full_name: profile?.full_name || '',
    company: profile?.company || '',
    position: profile?.position || '',
  });
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const fileInputRef = useRef(null);

  const initials = (form?.full_name || user?.email || 'U')?.split(' ')?.map((w) => w?.[0])?.join('')?.toUpperCase()?.slice(0, 2);

  const handleChange = (e) => setForm((p) => ({ ...p, [e?.target?.name]: e?.target?.value }));

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await onSave({ full_name: form?.full_name, company: form?.company, position: form?.position });
      showToast('Perfil actualizado correctamente', 'success');
    } catch {
      showToast('Error al guardar el perfil', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !user?.id) return;
    const ext = file?.name?.split('.')?.pop()?.toLowerCase();
    const path = `${user?.id}/avatar.${ext}`;
    setUploadingAvatar(true);
    try {
      const { error: uploadError } = await supabase?.storage
        ?.from('avatars')
        ?.upload(path, file, { upsert: true, contentType: file?.type });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase?.storage?.from('avatars')?.getPublicUrl(path);
      const publicUrl = urlData?.publicUrl;
      if (publicUrl) {
        await onSave({ avatar_url: publicUrl });
        setAvatarUrl(publicUrl + '?t=' + Date.now());
        showToast('Foto de perfil actualizada', 'success');
      }
    } catch (err) {
      showToast(err?.message || 'Error al subir la imagen', 'error');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const validatePw = () => {
    const errs = {};
    if (!pwForm?.current) errs.current = 'Requerido';
    if (!pwForm?.newPw || pwForm?.newPw?.length < 8) errs.newPw = 'Mínimo 8 caracteres';
    if (pwForm?.newPw !== pwForm?.confirm) errs.confirm = 'Las contraseñas no coinciden';
    return errs;
  };

  const handleChangePassword = async () => {
    const errs = validatePw();
    if (Object.keys(errs)?.length > 0) { setPwErrors(errs); return; }
    setPwErrors({});
    setSavingPw(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ password: pwForm?.newPw });
      if (error) throw error;
      setPwForm({ current: '', newPw: '', confirm: '' });
      showToast('Contraseña actualizada', 'success');
    } catch (err) {
      showToast(err?.message || 'Error al cambiar contraseña', 'error');
    } finally {
      setSavingPw(false);
    }
  };

  const handleChangeEmail = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newEmail || !emailRegex?.test(newEmail)) {
      setEmailError('Introduce un email válido');
      return;
    }
    setEmailError('');
    setSavingEmail(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ email: newEmail });
      if (error) throw error;
      setEmailSent(true);
      setNewEmail('');
    } catch (err) {
      setEmailError(err?.message || 'Error al cambiar el email');
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Avatar + Basic Info */}
      <div className="flex items-start gap-6">
        <div className="flex-shrink-0 relative">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Avatar de perfil"
              className="w-20 h-20 rounded-2xl object-cover"
              style={{ border: '2px solid var(--color-border)' }}
            />
          ) : (
            <div
              className="flex items-center justify-center w-20 h-20 rounded-2xl text-white font-bold text-2xl"
              style={{ background: 'var(--color-accent)', fontFamily: '"Outfit", sans-serif' }}
            >
              {initials}
            </div>
          )}
          {uploadingAvatar && (
            <div className="absolute inset-0 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
              <Icon name="Loader2" size={20} color="white" strokeWidth={2} className="animate-spin" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <p className="text-foreground font-semibold" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.125rem' }}>
            {form?.full_name || 'Sin nombre'}
          </p>
          <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>
            {user?.email}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarUpload}
          />
          <button
            onClick={() => fileInputRef?.current?.click()}
            disabled={uploadingAvatar}
            className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors text-foreground disabled:opacity-60"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
          >
            <Icon name="Camera" size={13} color="currentColor" strokeWidth={2} />
            {uploadingAvatar ? 'Subiendo...' : 'Cambiar foto'}
          </button>
        </div>
      </div>

      {/* Profile Fields */}
      <div className="rounded-xl border border-border p-6 space-y-5" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Información de perfil</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Nombre completo</label>
            <input
              name="full_name"
              value={form?.full_name}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 transition-colors"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
              placeholder="Tu nombre completo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Email</label>
            <div className="flex items-center gap-2">
              <input
                value={user?.email || ''}
                readOnly
                className="flex-1 px-3 py-2.5 rounded-lg border border-border bg-muted text-muted-foreground cursor-not-allowed"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
              />
              <button
                onClick={() => { setShowEmailForm(v => !v); setEmailSent(false); setEmailError(''); }}
                className="text-xs font-medium px-2.5 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-foreground whitespace-nowrap"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                Cambiar email
              </button>
            </div>
            {showEmailForm && !emailSent && (
              <div className="mt-2 flex flex-col gap-1.5">
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e?.target?.value)}
                  placeholder="Nuevo email"
                  className="w-full px-3 py-2 rounded-lg border bg-background text-foreground focus:outline-none transition-colors text-sm"
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif', borderColor: emailError ? 'var(--color-error)' : 'var(--color-border)' }}
                />
                {emailError && <p className="text-xs" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{emailError}</p>}
                <button
                  onClick={handleChangeEmail}
                  disabled={savingEmail}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors disabled:opacity-60"
                  style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
                >
                  {savingEmail ? <Icon name="Loader2" size={12} color="white" strokeWidth={2} className="animate-spin" /> : null}
                  {savingEmail ? 'Enviando...' : 'Confirmar cambio'}
                </button>
              </div>
            )}
            {emailSent && (
              <p className="text-xs mt-1.5" style={{ color: 'var(--color-success)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Te hemos enviado un email de confirmación al nuevo correo
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Empresa</label>
            <input
              name="company"
              value={form?.company}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 transition-colors"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
              placeholder="Nombre de la empresa"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Cargo / Posición</label>
            <input
              name="position"
              value={form?.position}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 transition-colors"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
              placeholder="Tu cargo en la empresa"
            />
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white transition-colors disabled:opacity-60"
            style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}
          >
            {saving ? <Icon name="Loader2" size={14} color="white" strokeWidth={2} className="animate-spin" /> : <Icon name="Save" size={14} color="white" strokeWidth={2.5} />}
            {saving ? 'Guardando...' : 'Guardar perfil'}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div className="rounded-xl border border-border p-6 space-y-5" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Cambiar contraseña</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[{ key: 'current', label: 'Contraseña actual', placeholder: '••••••••' },
            { key: 'newPw', label: 'Nueva contraseña', placeholder: 'Mín. 8 caracteres' },
            { key: 'confirm', label: 'Confirmar contraseña', placeholder: 'Repetir nueva contraseña' }]?.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{label}</label>
              <input
                type="password"
                value={pwForm?.[key]}
                onChange={(e) => setPwForm((p) => ({ ...p, [key]: e?.target?.value }))}
                className={`w-full px-3 py-2.5 rounded-lg border bg-background text-foreground focus:outline-none focus:ring-2 transition-colors ${
                  pwErrors?.[key] ? 'border-error' : 'border-border'
                }`}
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
                placeholder={placeholder}
              />
              {pwErrors?.[key] && (
                <p className="text-xs mt-1" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{pwErrors?.[key]}</p>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-2">
          <button
            onClick={handleChangePassword}
            disabled={savingPw}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white transition-colors disabled:opacity-60"
            style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}
          >
            {savingPw ? <Icon name="Loader2" size={14} color="white" strokeWidth={2} className="animate-spin" /> : <Icon name="Lock" size={14} color="white" strokeWidth={2.5} />}
            {savingPw ? 'Actualizando...' : 'Cambiar contraseña'}
          </button>
        </div>
      </div>
    </div>
  );
}
